import './ScalingSliderCard.scss';
import { ReactElement, useState } from 'react';
import {
  formatEfficiencyValue,
  formatOutcomeValue,
  formatSpendValue
} from '../BudgetBuilder/budgetBuilderFormat';
import { LinkedSlider } from '../components/LinkedSlider';
import { BudgetOutcome } from '../BudgetBuilder/budgetBuilderContract';
import {
  EfficiencyMetric,
  clampIndex,
  derivedEfficiency,
  nearestIndexBy,
  rangeOf
} from '../curveSliderMath';
import { lastHistoricalIndex } from './scaling-math';
import { ScalingCurvePoint, ScalingOutcomeData } from './scalingData';

interface ScalingSliderCardProps {
  outcomeData: ScalingOutcomeData;
  /** KPI name, prefixed onto the outcome label per the "{KPI} Volume/Revenue" rule. */
  kpi: string;
}

const effAt = (metric: EfficiencyMetric, p: ScalingCurvePoint) =>
  derivedEfficiency(metric, p.spend, p.fit, p.lwrConf, p.uprConf);

const ciText = (
  lower: number | null,
  upper: number | null,
  fmt: (v: number | null) => string
): string | undefined =>
  lower != null && upper != null
    ? `${fmt(Math.min(lower, upper))} – ${fmt(Math.max(lower, upper))}`
    : undefined;

/**
 * Three interlinked sliders (Weekly Spend / Outcome / Efficiency) bound to one
 * point on the fitted scaling curve — the same interaction as the Budget
 * Builder's BudgetCard, reusing LinkedSlider and the shared curve math. Drag any
 * slider to move the shared point; each is positioned by its OWN value within its
 * OWN range, so as spend rises volume and efficiency move to their own positions.
 * Only fitted points are selectable — a drag snaps to the nearest one. Defaults
 * to the last historical point ("current max spend"), the natural scaling anchor.
 */
export const ScalingSliderCard = ({ outcomeData, kpi }: ScalingSliderCardProps): ReactElement => {
  const { curve } = outcomeData;
  const metric: EfficiencyMetric = outcomeData.efficiencyMetric === 'ROAS' ? 'ROAS' : 'CPA';
  const outcome: BudgetOutcome = metric === 'ROAS' ? 'revenue' : 'volume';
  const outcomeWord = outcome === 'revenue' ? 'Revenue' : 'Volume';

  const maxIndex = curve.length - 1;
  const [activeIndex, setActiveIndex] = useState(() => lastHistoricalIndex(curve));
  const setIndex = (i: number): void => setActiveIndex(clampIndex(i, maxIndex));

  const point = curve[clampIndex(activeIndex, maxIndex)];
  const activeEff = effAt(metric, point);

  const [spendMin, spendMax] = rangeOf(curve, p => p.spend);
  const [fitMin, fitMax] = rangeOf(curve, p => p.fit);
  const [effMin, effMax] = rangeOf(curve, p => effAt(metric, p).value);

  return (
    <div className='scaling-slider-card' data-testid='scaling-slider-card'>
      <div className='scaling-slider-card-head'>
        <span className='scaling-slider-card-title'>Explore spend levels</span>
        <span className='scaling-slider-card-caption'>
          Drag any slider — the point snaps to the nearest fitted spend level. Starts at your
          current max spend.
        </span>
      </div>
      <div className='scaling-slider-card-sliders'>
        <LinkedSlider
          label='Weekly Spend'
          value={point.spend}
          min={spendMin}
          max={spendMax}
          valueText={formatSpendValue(point.spend)}
          onValueChange={v => setIndex(nearestIndexBy(curve, p => p.spend, v))}
        />
        <LinkedSlider
          label={`Weekly ${kpi} ${outcomeWord}`}
          value={point.fit}
          min={fitMin}
          max={fitMax}
          valueText={formatOutcomeValue(outcome, point.fit)}
          ciText={ciText(point.lwrConf, point.uprConf, v => formatOutcomeValue(outcome, v))}
          onValueChange={v => setIndex(nearestIndexBy(curve, p => p.fit, v))}
        />
        <LinkedSlider
          label={`Weekly ${metric}`}
          value={activeEff.value}
          min={effMin}
          max={effMax}
          valueText={formatEfficiencyValue(metric, activeEff.value)}
          ciText={ciText(activeEff.lower, activeEff.upper, v => formatEfficiencyValue(metric, v))}
          onValueChange={v => setIndex(nearestIndexBy(curve, p => effAt(metric, p).value, v))}
        />
      </div>
    </div>
  );
};
